/**
 * Interface Admin - Gestion Financière Complète
 * Supervision des paiements, dépôts, payouts et analytics financiers
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Tab,
  Tabs,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Badge,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  LinearProgress,
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemButton,
  Switch,
  FormControlLabel,
  AccordionDetails,
  AccordionSummary,
  Accordion
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  AccountBalance as BankIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Receipt as ReceiptIcon,
  CreditCard as CardIcon,
  AccountBalanceWallet as WalletIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Block as BlockIcon,
  ExpandMore as ExpandMoreIcon,
  Timeline as TimelineIcon,
  Assessment as ReportIcon,
  Notification as NotificationIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import disputeAdminService from '../../services/disputeAdminService';
import penaltyAdminService from '../../services/penaltyAdminService';
import AdminPayins from '../../components/admin/AdminPayins';
import AdminDeposits from '../../components/admin/AdminDeposits';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`financial-tabpanel-${index}`}
      aria-labelledby={`financial-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AdminFinances = () => {
  const { user } = useAuth();
  
  // Charger l'onglet mémorisé depuis localStorage ou utiliser 0 par défaut
  const [tabValue, setTabValue] = useState(() => {
    const savedTab = localStorage.getItem('adminFinancesTab');
    return savedTab ? parseInt(savedTab, 10) : 0;
  });

  // Mémoriser l'onglet actif dans localStorage quand il change
  useEffect(() => {
    localStorage.setItem('adminFinancesTab', tabValue.toString());
  }, [tabValue]);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [fraudAlerts, setFraudAlerts] = useState([]);
  const [auditTrail, setAuditTrail] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [penalties, setPenalties] = useState([]);
  const [disputesLoading, setDisputesLoading] = useState(false);
  const [penaltiesLoading, setPenaltiesLoading] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState(null);
  
  const [filters, setFilters] = useState({
    status: '',
    dateFrom: null,
    dateTo: null,
    search: '',
    amount: '',
    method: ''
  });
  
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [reconciliationDialog, setReconciliationDialog] = useState(false);

  useEffect(() => {
    loadDashboard();
    loadPayments();
    loadDeposits();
    loadPayouts();
    loadFraudAlerts();
    loadAuditTrail();
    loadDisputes();
    loadPenalties();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await api.get('/admin/finances/dashboard');
      console.log('Dashboard data received:', response.data);
      
      // Vérification de la structure des données
      const dashboardData = response.data.data;
      console.log('Structure du dashboard:', {
        'detailed_metrics': dashboardData.detailed_metrics ? Object.keys(dashboardData.detailed_metrics) : 'absent',
        'analytics': dashboardData.analytics ? Object.keys(dashboardData.analytics) : 'absent',
        'overview': dashboardData.overview ? Object.keys(dashboardData.overview) : 'absent'
      });
      
      setDashboardData(dashboardData);
    } catch (error) {
      console.error('Erreur chargement dashboard financier:', error);
    }
  };

  const loadPayments = async () => {
    try {
      setLoading(true);
      const params = { ...filters, type: 'payments' };
      const response = await api.get('/admin/finances/payments', { params });
      
      // Garantir que payments est toujours un tableau valide
      const data = response?.data?.data;
      if (data && data.payments && Array.isArray(data.payments)) {
        setPayments(data.payments);
      } else {
        console.warn('Invalid payments data received:', data);
        setPayments([]);
      }
    } catch (error) {
      console.error('Erreur chargement paiements:', error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDeposits = async () => {
    try {
      const response = await api.get('/admin/finances/deposits');
      setDeposits(response.data.data);
    } catch (error) {
      console.error('Erreur chargement dépôts:', error);
    }
  };

  const loadPayouts = async () => {
    try {
      const response = await api.get('/admin/finances/payouts');
      setPayouts(response.data.data);
    } catch (error) {
      console.error('Erreur chargement payouts:', error);
    }
  };

  const loadFraudAlerts = async () => {
    try {
      const response = await api.get('/admin/finances/fraud-detection');
      setFraudAlerts(response.data.data);
    } catch (error) {
      console.error('Erreur chargement alertes fraude:', error);
    }
  };

  const loadAuditTrail = async () => {
    try {
      const response = await api.get('/admin/finances/audit');
      setAuditTrail(response.data.data);
    } catch (error) {
      console.error('Erreur chargement audit trail:', error);
    }
  };

  const loadDisputes = async () => {
    try {
      setDisputesLoading(true);
      const response = await disputeAdminService.list({ limit: 5 });
      const items = response?.data || [];
      setDisputes(items);

      if (items.length === 0) {
        setSelectedDispute(null);
        return;
      }

      const selectedId = selectedDispute?.dispute_id || selectedDispute?.id;
      const matchingSelection = items.find((item) => {
        const itemId = item.dispute_id || item.id;
        return itemId === selectedId;
      });
      setSelectedDispute(matchingSelection || items[0]);
    } catch (error) {
      console.error('Erreur chargement litiges:', error);
    } finally {
      setDisputesLoading(false);
    }
  };

  const loadPenalties = async () => {
    try {
      setPenaltiesLoading(true);
      const response = await penaltyAdminService.list({ limit: 5 });
      setPenalties(response?.data || []);
    } catch (error) {
      console.error('Erreur chargement pénalités:', error);
    } finally {
      setPenaltiesLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const performPaymentAction = async (action, paymentId, data = {}) => {
    try {
      await api.post(`/admin/finances/payments/${paymentId}/actions`, {
        action,
        ...data
      });
      loadPayments();
      loadDashboard();
    } catch (error) {
      console.error('Erreur action paiement:', error);
    }
  };

  const performPayoutAction = async (action, payoutId) => {
    try {
      await api.post(`/admin/finances/payouts/${payoutId}/${action}`);
      loadPayouts();
      loadDashboard();
    } catch (error) {
      console.error('Erreur action payout:', error);
    }
  };

  const performReconciliation = async () => {
    try {
      await api.post('/admin/finances/reconciliation');
      loadDashboard();
      setReconciliationDialog(false);
    } catch (error) {
      console.error('Erreur réconciliation:', error);
    }
  };

  const exportFinancialData = async (type, format = 'csv') => {
    try {
      const response = await api.get('/admin/finances/export', {
        params: { type, format, ...filters },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erreur export:', error);
    }
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      'pending': 'warning',
      'completed': 'success',
      'failed': 'error',
      'refunded': 'info',
      'disputed': 'error'
    };
    return colors[status] || 'default';
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      'pending': 'En attente',
      'completed': 'Complété',
      'failed': 'Échoué',
      'refunded': 'Remboursé',
      'disputed': 'Disputé'
    };
    return labels[status] || status;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDateTime = (value) => {
    if (!value) {
      return '—';
    }
    try {
      return new Date(value).toLocaleString('fr-FR');
    } catch (error) {
      return value;
    }
  };

  const getDisputeStatusColor = (status) => {
    const colors = {
      'open': 'warning',
      'needs_support': 'info',
      'escalated': 'error',
      'resolved': 'success',
      'closed': 'default'
    };
    return colors[status] || 'default';
  };

  const getDisputeStatusLabel = (status) => {
    const labels = {
      'open': 'Ouvert',
      'needs_support': 'Support',
      'escalated': 'Escaladé',
      'resolved': 'Résolu',
      'closed': 'Clôturé'
    };
    return labels[status] || status;
  };

  const getPenaltyStatusColor = (status) => {
    const colors = {
      'pending': 'warning',
      'applied': 'info',
      'disputed': 'error',
      'waived': 'default',
      'collected': 'success',
      'cancelled': 'default',
      'refunded': 'success'
    };
    return colors[status] || 'default';
  };

  const getPenaltyStatusLabel = (status) => {
    const labels = {
      'pending': 'En attente',
      'applied': 'Appliquée',
      'disputed': 'Contestée',
      'waived': 'Levée',
      'collected': 'Prélevée',
      'cancelled': 'Annulée',
      'refunded': 'Remboursée'
    };
    return labels[status] || status;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        {/* En-tête */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" gutterBottom>
            💰 Gestion Financière Complète
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                loadDashboard();
                loadPayments();
                loadDeposits();
                loadPayouts();
              }}
            >
              Actualiser
            </Button>
            <Button
              variant="outlined"
              startIcon={<SecurityIcon />}
              onClick={() => setReconciliationDialog(true)}
            >
              Réconciliation
            </Button>
            <Button
              variant="contained"
              startIcon={<ExportIcon />}
              onClick={() => exportFinancialData('all', 'excel')}
            >
              Export Complet
            </Button>
          </Box>
        </Box>

        {/* Alertes de fraude */}
        {fraudAlerts.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6">
                ⚠️ {fraudAlerts.length} Alerte(s) de Fraude Détectée(s)
              </Typography>
            </Alert>
            {fraudAlerts.slice(0, 3).map((alert, index) => (
              <Alert key={index} severity="warning" sx={{ mb: 1 }}>
                {alert.message} - Score de risque: {alert.riskScore}%
              </Alert>
            ))}
          </Box>
        )}

        {/* Onglets */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <Tab label="Dashboard Financier" icon={<AnalyticsIcon />} />
            <Tab label="Paiements" icon={<CardIcon />} />
            <Tab label="Dépôts" icon={<WalletIcon />} />
            <Tab label="Payouts" icon={<BankIcon />} />
            <Tab label="Payins Propriétaires" icon={<MoneyIcon />} />
            <Tab label="Audit & Sécurité" icon={<SecurityIcon />} />
            <Tab label="Litiges & Pénalités" icon={<TimelineIcon />} />
            <Tab label="Reporting" icon={<ReportIcon />} />
          </Tabs>
        </Paper>

        {/* Dashboard Financier */}
        <TabPanel value={tabValue} index={0}>
          {dashboardData && (
            <Grid container spacing={3}>
              {/* KPIs Financiers */}
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <MoneyIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Revenus du Jour</Typography>
                    </Box>
                    <Typography variant="h4">
                      {formatCurrency(dashboardData.detailed_metrics?.revenue?.daily_revenue || 0)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      {(dashboardData.analytics?.trends?.growth_rate || 0) > 0 ? (
                        <TrendingUpIcon color="success" />
                      ) : (
                        <TrendingDownIcon color="error" />
                      )}
                      <Typography 
                        variant="body2" 
                        color={(dashboardData.analytics?.trends?.growth_rate || 0) > 0 ? 'success.main' : 'error.main'}
                      >
                        {(dashboardData.analytics?.trends?.growth_rate || 0).toFixed(1)}%
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CardIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Paiements Traités</Typography>
                    </Box>
                    <Typography variant="h4">
                      {dashboardData.detailed_metrics?.payments?.daily_count || 0}
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={dashboardData.detailed_metrics?.payments?.success_rate || 0}
                      sx={{ mt: 1 }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      Taux de succès: {(dashboardData.detailed_metrics?.payments?.success_rate || 0).toFixed(1)}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <BankIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Payouts Pending</Typography>
                    </Box>
                    <Typography variant="h4">
                      {formatCurrency(dashboardData.detailed_metrics?.payouts?.pending_amount || 0)}
                    </Typography>
                    <Typography variant="body2" color="warning.main">
                      {dashboardData.detailed_metrics?.payouts?.pending_count || 0} en attente
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <SecurityIcon color="error" sx={{ mr: 1 }} />
                      <Typography variant="h6">Alertes de Fraude</Typography>
                    </Box>
                    <Typography variant="h4" color="error">
                      {dashboardData.analytics?.risks?.fraud_alerts_count || 0}
                    </Typography>
                    <Button size="small" color="error" sx={{ mt: 1 }}>
                      Voir détails
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              {/* Graphiques et tendances */}
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Évolution des Revenus (30 jours)
                    </Typography>
                    <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography color="textSecondary">
                        Graphique des revenus
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Répartition des Méthodes de Paiement
                    </Typography>
                    <List dense>
                      {dashboardData.detailed_metrics?.payments?.by_method ? (
                        Object.entries(dashboardData.detailed_metrics.payments.by_method).map(([name, data], index) => (
                          <ListItem key={index}>
                            <Box sx={{ width: '100%' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2">
                                  {name}
                                </Typography>
                                <Typography variant="body2">
                                  {data.percentage || (data.count / dashboardData.detailed_metrics.payments.total_count * 100).toFixed(1)}%
                                </Typography>
                              </Box>
                              <LinearProgress 
                                variant="determinate" 
                                value={data.percentage || (data.count / dashboardData.detailed_metrics.payments.total_count * 100)}
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          </ListItem>
                        ))
                      ) : (
                        <Typography color="textSecondary">
                          Aucune donnée disponible
                        </Typography>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Indicateurs de santé financière */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Indicateurs de Santé Financière
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h5" color="success.main">
                            {(dashboardData.analytics?.trends?.cash_flow_percentage || 0).toFixed(1)}%
                          </Typography>
                          <Typography variant="body2">Cash Flow</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h5" color="primary.main">
                            {dashboardData.detailed_metrics?.payments?.average_transaction_amount || 0}€
                          </Typography>
                          <Typography variant="body2">Transaction Moyenne</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h5" color="warning.main">
                            {(dashboardData.analytics?.risks?.chargeback_rate || 0).toFixed(2)}%
                          </Typography>
                          <Typography variant="body2">Taux de Chargeback</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h5" color="info.main">
                            {dashboardData.detailed_metrics?.payments?.average_processing_time || 0}s
                          </Typography>
                          <Typography variant="body2">Temps de Traitement</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </TabPanel>

        {/* Gestion des Paiements */}
        <TabPanel value={tabValue} index={1}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Gestion des Paiements
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<ExportIcon />}
                  onClick={() => exportFinancialData('payments')}
                >
                  Exporter
                </Button>
              </Box>

              {/* Filtres pour paiements */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Rechercher"
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Statut</InputLabel>
                    <Select
                      value={filters.status}
                      onChange={(e) => setFilters({...filters, status: e.target.value})}
                    >
                      <MenuItem value="">Tous</MenuItem>
                      <MenuItem value="pending">En attente</MenuItem>
                      <MenuItem value="completed">Complété</MenuItem>
                      <MenuItem value="failed">Échoué</MenuItem>
                      <MenuItem value="refunded">Remboursé</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Méthode</InputLabel>
                    <Select
                      value={filters.method}
                      onChange={(e) => setFilters({...filters, method: e.target.value})}
                    >
                      <MenuItem value="">Toutes</MenuItem>
                      <MenuItem value="card">Carte</MenuItem>
                      <MenuItem value="bank_transfer">Virement</MenuItem>
                      <MenuItem value="paypal">PayPal</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="Date début"
                    value={filters.dateFrom}
                    onChange={(date) => setFilters({...filters, dateFrom: date})}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="Date fin"
                    value={filters.dateTo}
                    onChange={(date) => setFilters({...filters, dateTo: date})}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} md={1}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={loadPayments}
                    sx={{ height: '56px' }}
                  >
                    Filtrer
                  </Button>
                </Grid>
              </Grid>

              {/* Tableau des paiements */}
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID Transaction</TableCell>
                        <TableCell>Utilisateur</TableCell>
                        <TableCell>Montant</TableCell>
                        <TableCell>Méthode</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.isArray(payments) ? payments.map((payment) => (
                        <TableRow key={payment.id || Math.random().toString()}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              #{payment.transactionId || payment.reference_number || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ mr: 1, width: 32, height: 32 }}>
                                {payment.User?.name?.charAt(0) || 'U'}
                              </Avatar>
                              <Box>
                                <Typography variant="body2">
                                  {payment.User?.name || 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {payment.User?.email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency(payment.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={payment.paymentMethod}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getPaymentStatusLabel(payment.status)}
                              color={getPaymentStatusColor(payment.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(payment.createdAt).toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={(e) => {
                                setActionMenuAnchor(e.currentTarget);
                                setSelectedTransaction(payment);
                              }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            Aucun paiement trouvé
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        {/* Dépôts */}
        <TabPanel value={tabValue} index={2}>
          <AdminDeposits />
        </TabPanel>

        {/* Payouts */}
        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>
            Gestion des Payouts Propriétaires
          </Typography>
          <Typography color="textSecondary">
            Interface de gestion des payouts en développement...
          </Typography>
        </TabPanel>

        {/* Payins Propriétaires */}
        <TabPanel value={tabValue} index={4}>
          <AdminPayins />
        </TabPanel>

        {/* Audit & Sécurité */}
        <TabPanel value={tabValue} index={5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Audit Trail Financier
                  </Typography>
                  <List dense>
                    {auditTrail.slice(0, 10).map((entry, index) => (
                      <ListItem key={index}>
                        <Box>
                          <Typography variant="body2">
                            {entry.action} - {entry.description}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(entry.timestamp).toLocaleString()} - {entry.adminUser}
                          </Typography>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Alertes de Sécurité
                  </Typography>
                  {fraudAlerts.map((alert, index) => (
                    <Accordion key={index}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <WarningIcon color="error" sx={{ mr: 1 }} />
                          <Typography variant="body2">
                            {alert.type} - Score: {alert.riskScore}%
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2">
                          {alert.description}
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <Button size="small" color="error" variant="outlined">
                            Enquêter
                          </Button>
                          <Button size="small" sx={{ ml: 1 }}>
                            Marquer comme traité
                          </Button>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                  {fraudAlerts.length === 0 && (
                    <Typography color="textSecondary">
                      Aucune alerte de sécurité active
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Litiges & Pénalités */}
        <TabPanel value={tabValue} index={6}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h6">Litiges récents</Typography>
                      <Typography variant="body2" color="textSecondary">
                        Synchronisés avec l'API disputes
                      </Typography>
                    </Box>
                    <Button size="small" startIcon={<RefreshIcon />} onClick={loadDisputes}>
                      Actualiser
                    </Button>
                  </Box>
                  {disputesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : disputes.length > 0 ? (
                    <List dense>
                      {disputes.map((dispute, index) => {
                        const disputeId = dispute.dispute_id || dispute.id;
                        const disputeKey = disputeId || `dispute-${index}`;
                        const reservationRef = dispute.reservation?.id || dispute.reservation_id;
                        const isSelected = selectedDispute && (selectedDispute.dispute_id || selectedDispute.id) === disputeId;
                        return (
                          <ListItemButton
                            key={disputeKey}
                            selected={Boolean(isSelected)}
                            onClick={() => setSelectedDispute(dispute)}
                          >
                            <Box sx={{ width: '100%' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  {dispute.translated_reason || dispute.reason}
                                </Typography>
                                <Chip
                                  label={getDisputeStatusLabel(dispute.status)}
                                  color={getDisputeStatusColor(dispute.status)}
                                  size="small"
                                />
                              </Box>
                              <Typography variant="body2" color="textSecondary">
                                Réservation {reservationRef ? String(reservationRef).slice(0, 8) : '—'} · Priorité {dispute.priority}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                Mise à jour {formatDateTime(dispute.updated_at)}
                              </Typography>
                            </Box>
                          </ListItemButton>
                        );
                      })}
                    </List>
                  ) : (
                    <Typography color="textSecondary">
                      Aucun litige n'a été remonté sur la période.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Chronologie du litige
                  </Typography>
                  {selectedDispute ? (
                    <>
                      <Typography variant="subtitle1">
                        {selectedDispute.translated_reason || selectedDispute.reason}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        #{String(selectedDispute.dispute_id || selectedDispute.id || '').slice(0, 8)} · {getDisputeStatusLabel(selectedDispute.status)}
                      </Typography>
                      {(selectedDispute.events || []).length > 0 ? (
                        <List dense>
                          {(selectedDispute.events || []).slice(0, 5).map((event) => (
                            <ListItem key={event.id || `${event.event_type}-${event.created_at}`}>
                              <ListItemIcon>
                                <TimelineIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={event.translated_message || event.message || '—'}
                                secondary={`${event.event_type} · ${formatDateTime(event.created_at)}`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography color="textSecondary">
                          Aucun événement enregistré pour ce litige.
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography color="textSecondary">
                      Sélectionnez un litige pour consulter sa chronologie.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h6">Pénalités actives</Typography>
                      <Typography variant="body2" color="textSecondary">
                        Flux connecté aux API pénalités
                      </Typography>
                    </Box>
                    <Button size="small" startIcon={<RefreshIcon />} onClick={loadPenalties}>
                      Actualiser
                    </Button>
                  </Box>
                  {penaltiesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : penalties.length > 0 ? (
                    <List dense>
                      {penalties.map((penalty, index) => (
                        <ListItem key={penalty.id || penalty.penalty_id || `penalty-${index}`} alignItems="flex-start">
                          <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="subtitle1">
                                {penalty.translated_reason || penalty.reason}
                              </Typography>
                              <Chip
                                label={getPenaltyStatusLabel(penalty.status)}
                                color={getPenaltyStatusColor(penalty.status)}
                                size="small"
                              />
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                              {penalty.penalty_type} · {formatCurrency(Number(penalty.amount) || 0)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Dernière mise à jour {formatDateTime(penalty.updated_at)}
                            </Typography>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography color="textSecondary">
                      Aucune pénalité active.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Reporting */}
        <TabPanel value={tabValue} index={7}>
          <Typography variant="h6" gutterBottom>
            Reporting Financier Avancé
          </Typography>
          <Typography color="textSecondary">
            Fonctionnalité de reporting en développement...
          </Typography>
        </TabPanel>

        {/* Dialog de réconciliation */}
        <Dialog open={reconciliationDialog} onClose={() => setReconciliationDialog(false)}>
          <DialogTitle>Réconciliation des Comptes</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Cette action va vérifier la cohérence entre tous les paiements, dépôts et payouts.
            </Typography>
            <Alert severity="warning">
              La réconciliation peut prendre plusieurs minutes selon le volume de données.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReconciliationDialog(false)}>
              Annuler
            </Button>
            <Button 
              variant="contained" 
              onClick={performReconciliation}
              color="primary"
            >
              Lancer la Réconciliation
            </Button>
          </DialogActions>
        </Dialog>

        {/* Menu d'actions pour transactions */}
        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={() => setActionMenuAnchor(null)}
        >
          <MenuItem onClick={() => {/* Voir détails */}}>
            <ListItemIcon><ViewIcon /></ListItemIcon>
            <ListItemText>Voir détails</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {/* Rembourser */}}>
            <ListItemIcon><CancelIcon /></ListItemIcon>
            <ListItemText>Rembourser</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {/* Marquer frauduleux */}}>
            <ListItemIcon><BlockIcon /></ListItemIcon>
            <ListItemText>Marquer frauduleux</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => {/* Envoyer reçu */}}>
            <ListItemIcon><EmailIcon /></ListItemIcon>
            <ListItemText>Envoyer reçu</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </LocalizationProvider>
  );
};

export default AdminFinances;
